import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { XIcon } from 'lucide-react';
import { useEffect, useId, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useRevalidateOnLanguageChange } from '@/i18n/use-revalidate-on-language-change';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import type { Organization } from '@/common/organization';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { addOrganizationOptions } from '@/queries/organizations/add-orgnization';
import { updateOrganizationOptions } from '@/queries/organizations/update-orgnization';

const buildOrganizationSchema = (t: TFunction) =>
	z.object({
		name: z.string().min(1, t('admin.organizations.validation.nameRequired')),
	});
type OrganizationFormValues = z.infer<ReturnType<typeof buildOrganizationSchema>>;

export type OrganizationDrawerProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	organization: Organization | null;
	onOrgChange: () => void;
};
export function OrganizationDrawer({ open, onOpenChange, organization, onOrgChange }: OrganizationDrawerProps) {
	const { t } = useTranslation();
	const orgFormSchema = useMemo(() => buildOrganizationSchema(t), [t]);
	const orgForm = useForm<OrganizationFormValues>({
		resolver: zodResolver(orgFormSchema),
	});
	useRevalidateOnLanguageChange(orgForm);
	const createOrgFormId = useId();

	const addOrganizationApi = useMutation(addOrganizationOptions());
	const updateOrganizationApi = useMutation(updateOrganizationOptions());

	const handleSubmit = async (values: OrganizationFormValues) => {
		if (!values.name.trim()) {
			orgForm.setError('name', { message: t('admin.organizations.validation.nameRequired') });
			return;
		}
		if (organization?.id) {
			await updateOrganizationApi.mutateAsync({
				id: organization.id,
				payload: { name: values.name.trim() },
			});
		} else {
			await addOrganizationApi.mutateAsync({ name: values.name.trim() });
		}
		onOrgChange();
		onOpenChange(false);
		orgForm.reset();
	};

	useEffect(() => {
		if (organization) {
			orgForm.reset({
				name: organization.name || '',
			});
		} else {
			orgForm.reset();
		}
	}, [organization, orgForm.reset]);

	return (
		<Drawer direction={'right'} open={open} onOpenChange={onOpenChange}>
			<DrawerContent className="data-[vaul-drawer-direction=bottom]:max-h-[50vh] data-[vaul-drawer-direction=top]:max-h-[50vh] w-full max-w-md">
				<DrawerHeader className="flex flex-row justify-between border-b">
					<DrawerTitle>
						{organization ? t('admin.organizations.drawer.editTitle') : t('admin.organizations.create')}
					</DrawerTitle>
					<DrawerClose asChild>
						<Button
							variant="ghost"
							size="icon-lg"
							type="button"
							className="text-muted-foreground transition-colors hover:bg-transparent"
						>
							<XIcon className="size-5" />
						</Button>
					</DrawerClose>
				</DrawerHeader>
				<div className="no-scrollbar overflow-y-auto px-6 py-6 space-y-5">
					<form noValidate id={createOrgFormId} onSubmit={orgForm.handleSubmit(handleSubmit)}>
						<Controller
							name="name"
							control={orgForm.control}
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<FieldLabel htmlFor={field.name}>
										{t('admin.organizations.drawer.nameLabel')} <span className="text-red-600">*</span>
									</FieldLabel>
									<Input
										{...field}
										id={field.name}
										aria-invalid={fieldState.invalid}
										placeholder={t('admin.organizations.drawer.namePlaceholder')}
										autoComplete="off"
										className="bg-input"
										required
									/>
									{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
								</Field>
							)}
						/>
					</form>
				</div>
				<DrawerFooter className="flex flex-row items-center justify-end gap-3 px-6 py-4 border-t mt-0">
					<DrawerClose asChild>
						<Button type="button" variant="outline">
							{t('common.actions.cancel')}
						</Button>
					</DrawerClose>
					<Button
						type="submit"
						form={createOrgFormId}
						disabled={
							!orgForm.formState.isValid ||
							orgForm.formState.disabled ||
							addOrganizationApi.isPending ||
							updateOrganizationApi.isPending
						}
						className="disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{addOrganizationApi.isPending || updateOrganizationApi.isPending
							? t('common.states.saving')
							: t('common.actions.save')}
					</Button>
				</DrawerFooter>
			</DrawerContent>
		</Drawer>
	);
}
